import {
  useDaily,
  useDailyEvent,
  useMeetingState,
} from "@daily-co/daily-react";
import { useToastController } from "@tamagui/toast";
import { useCallback, useState } from "react";

import { useGetClinicStatusForPatient } from "@/api/generated";
import { useSetVideoVisitRecordingConsent } from "@/api/mutations/videoVisitConsent";
import { useGetVideoVisitRecordingConsent } from "@/api/queries/videoVisitConsent";
import {
  Adapt,
  Button,
  Dialog,
  Heading,
  Link,
  Paragraph,
  Sheet,
  YStack,
} from "@/components";
import { useFeatureFlag } from "@/components/FeatureFlagProvider";
import { captureException } from "@/sentry";

/**
 * In-call recording/AI consent prompt (av-call-recording-consent-v2).
 *
 * Consent is asked AFTER the patient has joined the call, and a "Yes" both
 * records consent AND starts Daily transcription on the spot (the room is
 * provisioned transcription-capable but idle until now).
 *
 * The decision is final for the visit — declining dismisses the modal and it
 * does not reappear (the clinician-attested fallback, a later phase, is the
 * backstop for "patient wasn't asked / skipped").
 */
const InCallConsentModal = () => {
  // Transcription model for the client-initiated startTranscription() below.
  // Reads the same flag as the room's auto_transcription_settings (new-api),
  // so both start paths use the same model.
  const { value: transcriptionModel } = useFeatureFlag(
    "av-call-transcription-model",
    "nova-2-medical",
  );
  const { data: clinicStatus } = useGetClinicStatusForPatient();
  const encounterId = clinicStatus?.conversation?.curai_encounter_id;
  const { data, isLoading } = useGetVideoVisitRecordingConsent(encounterId);
  const { mutate, isPending } = useSetVideoVisitRecordingConsent(encounterId);

  const callObject = useDaily();
  const meetingState = useMeetingState();
  const { show } = useToastController();

  // Final-for-the-visit: set once the patient consents or declines so the modal
  // never reappears, even though the persisted decline value is just `false`.
  const [decided, setDecided] = useState(false);
  // If "Yes" is tapped before the call is fully joined, defer the start to the
  // `joined-meeting` event (startTranscription needs a joined call object).
  const [startWhenJoined, setStartWhenJoined] = useState(false);

  const startTranscription = useCallback(() => {
    // Returns void; success/failure surface via the daily events below.
    // Pass transcription settings explicitly — a client start doesn't inherit
    // the room's config.
    callObject?.startTranscription({
      model: transcriptionModel,
      punctuate: true,
      extra: { smart_format: true, numerals: true },
    });
  }, [callObject, transcriptionModel]);

  useDailyEvent(
    "joined-meeting",
    useCallback(() => {
      if (startWhenJoined) {
        startTranscription();
        setStartWhenJoined(false);
      }
    }, [startWhenJoined, startTranscription]),
  );

  useDailyEvent(
    "transcription-error",
    useCallback(
      (event) => {
        captureException(
          new Error(
            `Daily transcription-error: ${event?.errorMsg ?? "unknown"}`,
          ),
          { extra: { encounterId } },
        );
        show("We couldn't start transcription for this visit.");
      },
      [encounterId, show],
    ),
  );

  const consent = () => {
    if (isPending) return;
    setDecided(true);
    mutate(true);
    if (meetingState === "joined-meeting") {
      startTranscription();
    } else {
      setStartWhenJoined(true);
    }
  };

  const decline = () => {
    if (isPending) return;
    setDecided(true);
    mutate(false);
  };

  // Show only when the patient has NOT yet made a decision this encounter:
  // recording_consent is null (never asked). A persisted true (consented) or
  // false (explicitly declined) is final and suppresses the prompt on later
  // calls in the same encounter. `decided` covers the in-session gap between
  // tapping a CTA and the consent query refetching.
  const open =
    !!encounterId && !isLoading && data?.recording_consent == null && !decided;

  // Modal decision is required — ignore backdrop/escape close; the two CTAs are
  // the only way out.
  const noop = () => {};

  return (
    <Dialog open={open} onOpenChange={noop}>
      <Adapt when="maxLg">
        <Sheet
          modal
          open={open}
          onOpenChange={noop}
          disableDrag
          snapPointsMode="fit"
          zIndex={1000000}
        >
          <Sheet.Frame
            backgroundColor="$fillModal"
            padding="$200"
            gap="$150"
            zIndex={1000001}
          >
            <Adapt.Contents />
          </Sheet.Frame>
          <Sheet.Overlay
            backgroundColor="$fillOverlay"
            backdropFilter="blur(8px)"
            zIndex={1000000}
          />
        </Sheet>
      </Adapt>
      <Dialog.Portal zIndex={1000000}>
        <Dialog.Overlay
          backgroundColor="$fillOverlay"
          backdropFilter="blur(8px)"
          zIndex={1000000}
        />
        <Dialog.Content
          backgroundColor="$fillModal"
          borderRadius="$150"
          padding="$200"
          minWidth={300}
          maxWidth={400}
          gap="$150"
          zIndex={1000001}
        >
          <Heading size="$lg">Help your clinician focus on you.</Heading>
          <Paragraph size="$sm" lineHeight="$md" color="$textSecondary">
            We use AI to help document your visit so your clinician can spend
            more time focused on you. All generated content is reviewed and
            signed by a licensed clinician before becoming part of your medical
            record. You can request deletion at{" "}
            <Link href="mailto:privacy@curai.com">privacy@curai.com</Link>
          </Paragraph>

          <YStack gap="$100" marginTop="$100">
            <Button
              mode="primary"
              loading={isPending}
              onPress={consent}
              dataTrackingId="video-in-call-consent-allow"
            >
              I consent to the use of AI tools
            </Button>
            <Button
              mode="tertiary"
              loading={isPending}
              onPress={decline}
              dataTrackingId="video-in-call-consent-decline"
            >
              I do not consent
            </Button>
          </YStack>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog>
  );
};

export default InCallConsentModal;
